import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from './cn';
import { Spinner } from './Spinner';

/**
 * The three buttons the supplied design actually contains — the red call to action
 * (`.btn-read`, `.livetv`, the newsletter submit), the bordered circle used by the hero
 * carousel (`.circ`), and the quiet text link (`.view-all`) — plus the sizes they appear
 * at. Variants are a closed set on purpose: "one more variant" is how a design system
 * stops being one.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-red text-white hover:bg-red-dark',
  secondary: 'border border-line bg-white text-ink hover:border-red hover:text-red',
  ghost: 'text-ink hover:text-red',
  link: 'text-ink hover:text-red underline-offset-4 hover:underline',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'text-xs px-3 py-2 gap-1.5',
  md: 'text-sm px-5 py-3 gap-2',
  lg: 'text-base px-6 py-3.5 gap-2',
};

const BASE =
  'inline-flex items-center justify-center rounded-md font-bold tracking-wide transition ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks interaction without changing the button's width. */
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, icon, className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={props.type ?? 'button'}
      // A loading button that is merely visually busy is still clickable by keyboard.
      disabled={props.disabled ?? loading}
      aria-busy={loading || undefined}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...props}
    >
      {loading ? <Spinner size={size === 'lg' ? 18 : 14} /> : icon}
      {children}
    </button>
  );
});

export interface ButtonLinkProps {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * A link that looks like a button. Separate from `Button` rather than a polymorphic `as`
 * prop: navigation is an anchor and an action is a button, and blurring the two is how
 * middle-click and "open in new tab" quietly stop working on a news site.
 */
export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  icon,
  className,
  children,
}: ButtonLinkProps) {
  return (
    <Link to={to} className={cn(BASE, VARIANTS[variant], SIZES[size], className)}>
      {icon}
      {children}
    </Link>
  );
}
